import {
  Box,
  Button,
  ButtonProps,
  Divider,
  Flex,
  HStack,
  InputProps,
  Text,
  Menu,
  MenuButton,
  MenuButtonProps,
  MenuItemOption,
  MenuList,
  MenuListProps,
  MenuOptionGroup,
  Portal,
  MenuItemOptionProps,
  useColorModeValue,
  FormControl,
  useFormControlContext,
  useDisclosure,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { Counter } from '~/packages/common-frontend/components/Counter';
import { Icon } from '~/packages/common-frontend/components/icons';
import { SearchBarInput } from '~/packages/common-frontend/components/SearchAndCopyComponents';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { FontWeights } from '~/packages/common-frontend/theme/types';
import { fontSizes } from '~/packages/common-frontend/theme/typography';

interface MultiSelectProps<T extends string = string> {
  'data-testid'?: string;
  onChange?: (nextValue: T, isOpen: boolean) => void;
  onMenuClosed?: () => void;
  onApplyOptions?: (selectedOptions?: T[]) => void;
  options: { label: string | React.ReactNode; value: string }[];
  selectedOptions: T[];
  optionsHeader?: string;
  applyButtonText?: string;
  titleText?: string;
  width?: React.ComponentProps<typeof Button>['width'];
  size?: 'sm' | 'md';
  buttonProps?: React.ComponentProps<typeof Button>;
}

interface NewMultiSelectProps<T extends string = string> {
  onApplyOptions: (selectedOptions: T[]) => void;
  options: { label: string | React.ReactNode; value: T }[];
  initialSelections: T[];
  optionsHeader?: string;
  showSelectedCount?: boolean;
  applyButtonText?: string;
  autoApplyOptions?: boolean;
  titleText: string;
  titleFontWeight?: FontWeights;
  counterLabel?: string;
  placeholderText?: string;
  fullWidth?: boolean;
  width?: string;
  zIndex?: number;
  colorScheme?: string;
  iconButton?: React.ReactElement;
  menuTopMargin?: string;
  buttonProps?: MenuButtonProps & ButtonProps;
  menuListProps?: MenuListProps;
  inputProps?: InputProps;
  isSearchable?: boolean;
  menuItemOptionProps?: MenuItemOptionProps;
  matchWidthPopupBreakpoint?: number;
  disabledOptionValues?: T[];
}

export function MultiSelect<T extends string = string>({
  'data-testid': testId = 'MultiSelect',
  onChange,
  onMenuClosed,
  onApplyOptions,
  options,
  selectedOptions,
  optionsHeader = '',
  applyButtonText = '',
  titleText = '',
  width = '100%',
  size = 'md',
  buttonProps,
}: MultiSelectProps<T>) {
  return (
    <Flex data-testid={testId} width={width}>
      <Menu
        data-testid={`${testId}.Menu`}
        size={size}
        matchWidth
        autoSelect={false}
        eventListeners={{ scroll: false, resize: true }}
        onClose={() => onMenuClosed?.()}
      >
        {({ onClose, isOpen }) => (
          <>
            <MenuButton
              as={Button}
              colorScheme="gray"
              fontWeight="normal"
              minWidth={size === 'md' ? '12.5rem' : '10rem'}
              overflow="hidden"
              rightIcon={<Icon icon="chevron-down" size={size} />}
              size={size}
              textAlign="left"
              variant="secondary-muted"
              width={width}
              {...buttonProps}
            >
              {titleText}
            </MenuButton>
            <MenuList>
              <MenuOptionGroup
                value={selectedOptions}
                title={optionsHeader}
                type="checkbox"
                fontSize={fontSizes[size === 'md' ? 'base' : size].fontSize}
              >
                {options.map(({ label, value }) => (
                  <MenuItemOption
                    data-testid={`${testId}.Menu.MenuItemOption`}
                    key={value}
                    value={value}
                    onClick={() => onChange?.(value as T, isOpen)}
                    closeOnSelect={false}
                    fontSize={fontSizes[size === 'md' ? 'base' : size].fontSize}
                  >
                    {label}
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>

              {applyButtonText && (
                <Flex direction="column" paddingX="0.5rem">
                  <Divider marginY="0.5rem" />
                  <Button
                    size="sm"
                    flexGrow={1}
                    variant="secondary-muted"
                    colorScheme="gray"
                    onClick={() => {
                      onApplyOptions?.(selectedOptions);
                      onClose();
                    }}
                  >
                    {applyButtonText}
                  </Button>
                </Flex>
              )}
            </MenuList>
          </>
        )}
      </Menu>
    </Flex>
  );
}

// helpers
// TODO: @haghanim, add searching by numbers too (ex: abc providers #1,#2,#3 should be differentiated)
// TODO: @haghanim, add testing to this function and dropdowns w/ ReactNodes
const getTextContentFromReactNode = (node: React.ReactNode): string => {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return node.toString();
  if (Array.isArray(node))
    return node.map(getTextContentFromReactNode).join('');
  if (React.isValidElement(node)) {
    return getTextContentFromReactNode(node.props.children); // @haghanim, this may throw an error
  }
  return '';
};

export function IsolatedMultiSelect<T extends string = string>({
  onApplyOptions,
  showSelectedCount = false,
  applyButtonText = 'Apply',
  autoApplyOptions = false,
  isSearchable,
  iconButton,
  initialSelections,
  inputProps,
  buttonProps,
  menuItemOptionProps,
  menuListProps,
  matchWidthPopupBreakpoint = 300, // width of popup will match that of menu button, if menu button width is greater than matchWidthPopupBreakpoint
  options,
  optionsHeader = '',
  titleText = '',
  titleFontWeight,
  counterLabel = 'Selected',
  placeholderText = 'Select',
  disabledOptionValues = [],
}: NewMultiSelectProps<T>) {
  const formControlContext = useFormControlContext();
  const [selectedOptions, setSelectedOptions] = useState<T[]>(
    initialSelections,
  );
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { isOpen, onClose, onToggle } = useDisclosure();

  useEffect(() => {
    setSelectedOptions(initialSelections);
  }, [initialSelections]);

  // if changes are not applied, then reset options to initial selections
  // so selected count is accurate
  useEffect(() => {
    if (!isOpen && !autoApplyOptions) {
      if (initialSelections.length !== selectedOptions.length) {
        setSelectedOptions(initialSelections);
      }
    }
  }, [autoApplyOptions, initialSelections, isOpen, selectedOptions.length]);

  useEffect(() => {
    const timeoutId = setTimeout(
      () => inputRef.current && inputRef.current.focus(),
      0,
    );
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const optionsFilteredBySearch = useMemo(() => {
    return options.filter((option) => {
      const labelText = getTextContentFromReactNode(option.label).toLowerCase();
      return labelText.includes(searchQuery.toLowerCase());
    });
  }, [options, searchQuery]);

  const onOptionChangeHandler = (value: T) => {
    const updatedOptions = selectedOptions.includes(value)
      ? selectedOptions.filter((option) => option !== value)
      : [...selectedOptions, value];
    setSelectedOptions(updatedOptions);
    if (autoApplyOptions) onApplyOptions(updatedOptions);
  };

  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [menuButtonWidth, setMenuButtonWidth] = useState(0);

  useEffect(() => {
    const onResize = _.debounce(() => {
      if (menuButtonRef.current) {
        setMenuButtonWidth(menuButtonRef.current.offsetWidth);
      }
    }, 100);

    onResize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const searchBarColor = useColorModeValue('gray.800', 'gray.400');
  const selectAllColor = useColorModeValue('blue.500', 'gray.200');
  const clearAllColor = useColorModeValue('blue.500', 'gray.200');

  return (
    <Menu
      preventOverflow
      matchWidth={menuButtonWidth >= matchWidthPopupBreakpoint}
      eventListeners={{ scroll: true, resize: true }}
      isOpen={isOpen}
      onClose={onClose}
    >
      <>
        {/* header */}
        <MenuButton
          ref={menuButtonRef}
          as={Button}
          variant="secondary-muted"
          fontWeight={400}
          {...buttonProps}
          colorScheme={
            formControlContext?.isInvalid ? 'red' : buttonProps?.colorScheme
          }
          rightIcon={<Icon icon="chevron-down" />}
          onClick={onToggle}
        >
          {iconButton ||
            (showSelectedCount &&
            selectedOptions.length > 0 &&
            selectedOptions.length !== options.length ? (
              <HStack>
                <Typography
                  trim
                  TextProps={{
                    noOfLines: 1,
                    wordBreak: 'break-all',
                    textAlign: 'left',
                  }}
                  fontWeight={titleFontWeight}
                >
                  {counterLabel}
                </Typography>
                <Counter count={selectedOptions.length} />
              </HStack>
            ) : (
              <Typography
                variant={
                  selectedOptions.length === 0 ? 'base-muted' : undefined
                }
                TextProps={{
                  noOfLines: 1,
                  wordBreak: 'break-all',
                  textAlign: 'left',
                }}
                inline
                fontWeight={titleFontWeight}
              >
                {selectedOptions.length === 0 ? placeholderText : titleText}
              </Typography>
            ))}
        </MenuButton>

        {/* // todo: @haghanim add bgColor={colors.background[100]} to the TXNs page */}
        {/* content */}
        <MenuList {...menuListProps}>
          {isSearchable && (
            <Flex
              mt="1rem"
              mb=".5rem"
              px="1.5rem"
              direction="column"
              alignItems="flex-start"
            >
              {optionsHeader && (
                <Text fontWeight="bold" mb="1.5rem">
                  {optionsHeader}
                </Text>
              )}
              <FormControl>
                <SearchBarInput
                  ref={inputRef}
                  query={searchQuery}
                  onChangeQuery={setSearchQuery}
                  inputProps={{
                    ...inputProps,
                    color: { searchBarColor },
                    _placeholder: { color: searchBarColor },
                  }}
                />
              </FormControl>
              <Flex my="1rem" gap={4}>
                <Button
                  variant="link"
                  colorScheme="blue"
                  color={selectAllColor}
                  cursor="pointer"
                  onClick={() => {
                    const updatedOptions = optionsFilteredBySearch.map(
                      (selectedOption) => selectedOption.value as T,
                    );
                    setSelectedOptions(updatedOptions);
                    if (autoApplyOptions) onApplyOptions(updatedOptions);
                  }}
                >
                  Select All
                </Button>
                <Button
                  variant="link"
                  colorScheme="blue"
                  color={clearAllColor}
                  cursor="pointer"
                  onClick={() => {
                    setSelectedOptions([]);
                    if (autoApplyOptions) onApplyOptions([]);
                    setSearchQuery('');
                  }}
                >
                  Clear All
                </Button>
              </Flex>
            </Flex>
          )}
          <Flex
            {...(isSearchable
              ? {
                  maxHeight: '200px',
                  direction: 'column',
                  px: '1.5rem',
                  pb: '4',
                  overflowY: 'auto',
                }
              : { display: 'block', maxHeight: '250px', overflowY: 'auto' })}
          >
            <MenuOptionGroup
              title={!isSearchable ? optionsHeader : undefined}
              value={selectedOptions}
              type="checkbox"
            >
              {(isSearchable ? optionsFilteredBySearch : options).map(
                ({ label, value }) => {
                  return (
                    <MenuItemOption
                      key={value}
                      value={value}
                      onClick={() => onOptionChangeHandler(value as T)}
                      closeOnSelect={false}
                      {...menuItemOptionProps}
                      isDisabled={disabledOptionValues.includes(value as T)}
                    >
                      {label}
                    </MenuItemOption>
                  );
                },
              )}
            </MenuOptionGroup>
          </Flex>

          {/* footer */}
          {!autoApplyOptions && (
            <>
              <Divider mt="1rem" />
              <Flex
                justify="space-between"
                gap={4}
                px={isSearchable ? '1.5rem' : '0.5rem'}
                my={isSearchable ? '1rem' : undefined}
                mt={!isSearchable ? '.5rem' : undefined}
              >
                <Button
                  variant={isSearchable ? 'primary' : 'secondary-muted'}
                  colorScheme={isSearchable ? 'v2-aurora' : 'gray'}
                  size={isSearchable ? undefined : 'sm'}
                  onClick={() => {
                    onApplyOptions(selectedOptions);
                    setSearchQuery('');
                    onClose();
                  }}
                  flex={1}
                >
                  {applyButtonText}
                </Button>
              </Flex>
            </>
          )}
        </MenuList>
      </>
    </Menu>
  );
}

export function IsolatedMultiSelectWithPortalIconButton<
  T extends string = string
>({
  onApplyOptions,
  initialSelections,
  options,
  optionsHeader = '',
  applyButtonText = '',
  icon,
  zIndex,
  buttonProps,
}: NewMultiSelectProps<T> & { icon: React.ReactElement }) {
  const [selectedOptions, setSelectedOptions] = useState<T[]>(
    initialSelections,
  );

  // if initial selections changes, we want to update the selected options
  useEffect(() => {
    setSelectedOptions(initialSelections);
  }, [initialSelections]);

  const onOptionChangeHandler = (value: T) => {
    const updatedOptions = [...selectedOptions];
    const index = updatedOptions.indexOf(value);
    if (index >= 0) {
      updatedOptions.splice(index, 1);
    } else {
      updatedOptions.push(value);
    }
    setSelectedOptions(updatedOptions);
  };

  return (
    <Menu
      matchWidth
      autoSelect={false}
      eventListeners={{ scroll: false, resize: true }}
    >
      {({ onClose }) => (
        <>
          <MenuButton {...buttonProps}>{icon}</MenuButton>
          <Portal>
            <MenuList maxH="250" maxW="100" overflowY="scroll" zIndex={zIndex}>
              <MenuOptionGroup
                value={selectedOptions}
                title={optionsHeader}
                type="checkbox"
              >
                {options.map(({ label, value }) => {
                  return (
                    <MenuItemOption
                      key={value}
                      value={value}
                      onClick={() => onOptionChangeHandler(value as T)}
                      closeOnSelect={false}
                      fontSize="12px"
                    >
                      {label}
                    </MenuItemOption>
                  );
                })}
              </MenuOptionGroup>

              {applyButtonText &&
                !_.isEqual(selectedOptions, initialSelections) && (
                  <>
                    <Divider width="90%" margin="5px auto" />
                    <Box mb="5px">
                      <Button
                        h="1.75rem"
                        size="sm"
                        w="90%"
                        mt="10px"
                        variant="outline"
                        fontWeight="normal"
                        onClick={() => {
                          onApplyOptions(selectedOptions);
                          onClose();
                        }}
                      >
                        {applyButtonText}
                      </Button>
                    </Box>
                  </>
                )}
            </MenuList>
          </Portal>
        </>
      )}
    </Menu>
  );
}

// type
type OptionType<T> = {
  id: string;
  label: React.ReactNode;
  value: T;
};

export const recordToOptionTypeArray = (
  record: Record<string, string>,
): OptionType<string>[] => {
  return Object.entries(record).map(([id, name]) => ({
    id,
    label: name,
    value: id,
  }));
};

export const setToOptionTypeArray = (set: Set<string>): OptionType<string>[] =>
  Array.from(set, (slug) => ({
    id: slug,
    label: commonUtils.toTitleCase(slug),
    value: slug,
  }));

export const arrayToOptionTypeArray = (
  arr: Array<string>,
): OptionType<string>[] =>
  arr.map((slug) => ({
    id: slug,
    label: commonUtils.toTitleCase(slug),
    value: slug,
  }));
